import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, Navigate } from 'react-router-dom';
import { LayoutDashboard, Image as ImageIcon, Settings, LogOut } from 'lucide-react';
import Studio from './pages/Studio';
import Admin from './pages/Admin';
import Gallery from './pages/Gallery';
import Login from './pages/Login';

function Layout({ children }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    const loadUser = () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (!token || !storedUser) {
        navigate('/login');
      } else {
        setUser(JSON.parse(storedUser));
      }
    };

    loadUser();
    window.addEventListener('userUpdate', loadUser);
    return () => window.removeEventListener('userUpdate', loadUser);
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200 flex flex-col">
        <div className="p-6 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
            <ImageIcon className="text-white w-5 h-5" />
          </div>
          <span className="font-bold text-xl text-slate-800">PhotoMaker</span>
        </div>
        
        <nav className="flex-1 px-4 py-4 space-y-2">
          <Link to="/studio" className="flex items-center gap-3 px-4 py-3 text-slate-600 rounded-xl hover:bg-slate-50 hover:text-blue-600 transition-colors font-medium">
            <LayoutDashboard className="w-5 h-5" />
            Studio
          </Link>
          <Link to="/gallery" className="flex items-center gap-3 px-4 py-3 text-slate-600 rounded-xl hover:bg-slate-50 hover:text-blue-600 transition-colors font-medium">
            <ImageIcon className="w-5 h-5" />
            My Gallery
          </Link>
          {user.role === 'admin' && (
            <Link to="/admin" className="flex items-center gap-3 px-4 py-3 text-slate-600 rounded-xl hover:bg-slate-50 hover:text-blue-600 transition-colors font-medium">
              <Settings className="w-5 h-5" />
              Admin
            </Link>
          )}
        </nav>
        
        <div className="p-4 border-t border-slate-200">
          <div className="bg-slate-50 rounded-xl p-4 mb-4 text-center">
            <p className="text-xs text-slate-500 mb-1 truncate">{user.email}</p>
            <p className="text-sm text-slate-600 mb-1">Available Credits</p>
            <p className={`text-2xl font-bold ${user.credits < 2 ? 'text-red-600' : 'text-slate-800'}`}>
              {user.credits}
            </p>
            {user.credits < 2 && (
              <p className="text-xs text-red-500 mt-1 font-medium">Low Balance!</p>
            )}
          </div>
          <button 
            onClick={handleLogout}
            className="flex items-center justify-center gap-2 w-full px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors font-medium"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-hidden relative">
        {user.credits < 2 && (
          <div className="absolute top-4 right-4 bg-red-100 border border-red-200 text-red-700 px-4 py-3 rounded-xl shadow-lg z-50 flex items-center gap-3">
            <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="font-bold text-sm">Low Credits</p>
              <p className="text-xs">You need at least 2 credits to remove backgrounds.</p>
            </div>
          </div>
        )}
        {children}
      </main>
    </div>
  );
}

// Helper to protect routes
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

const AdminRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  if (!token || user.role !== 'admin') {
    return <Navigate to="/studio" replace />;
  }
  return children;
};

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<ProtectedRoute><Layout><Studio /></Layout></ProtectedRoute>} />
        <Route path="/studio" element={<ProtectedRoute><Layout><Studio /></Layout></ProtectedRoute>} />
        <Route path="/gallery" element={<ProtectedRoute><Layout><Gallery /></Layout></ProtectedRoute>} />
        <Route path="/admin" element={<AdminRoute><Layout><Admin /></Layout></AdminRoute>} />
      </Routes>
    </Router>
  );
}

export default App;
